import { html, cx, useState, useRef, useEffect, useCallback, useMedia } from '../h.js'
import { Icon } from '../icons.js'
import { t } from '../i18n.js'
import { parseShorthand, duration, computeWaveform, fileSize } from '../format.js'
import { EmojiPicker, useToast } from '../components.js'
import { useApp } from '../store.js'
import { KIND, kindForMime, clockNow } from '@pine/core'
import { preparePhoto, describeVideo } from '../media-prep.js'
import { previewableUrl, fetchPreview } from '../link-preview.js'

const TYPING_THROTTLE = 2500
const MAX_VOICE_SECONDS = 300

/*
 * What "documents" means to a file picker.
 *
 * Extensions as well as MIME types, because Android's picker matches on
 * whichever the provider happens to report and a document from a cloud folder
 * often arrives with neither the type nor the name you would expect.
 */
const DOCUMENT_TYPES = [
  'application/pdf', 'application/msword', 'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint', 'application/rtf', 'text/plain', 'text/csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/epub+zip',
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.rtf', '.csv', '.odt', '.ods', '.epub'
].join(',')

/**
 * The composer.
 *
 * It is a contenteditable rather than a textarea so that a pasted image, an
 * emoji insert and multi-line text all behave the way people expect; the
 * plain-text value is read back with innerText, and formatting shorthand is
 * converted to stored entities on send.
 */
export function Composer ({
  room, canPost, replyTo, editing, onClearContext, onPending = null,
  bot = null, startable = false, notice = null
}) {
  const store = useApp()
  const toast = useToast()
  const inputRef = useRef(null)
  const roomRef = useRef(room)
  roomRef.current = room
  // Messages leave one after another, in the order they were sent; see send().
  const queue = useRef(Promise.resolve())
  const fileRef = useRef(null)
  const lastTyping = useRef(0)

  const emojiRef = useRef(null)
  const emojiButtonRef = useRef(null)
  const attachRef = useRef(null)
  const caret = useRef(null)
  const pickOriginal = useRef(false)

  const [draft, setDraft] = useState('')
  const [attachments, setAttachments] = useState([])
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [attachOpen, setAttachOpen] = useState(false)
  const [recording, setRecording] = useState(null)
  const [sending, setSending] = useState(false)
  const [preview, setPreview] = useState(null) // { url, data, dismissed }
  const [commandsOpen, setCommandsOpen] = useState(false)

  // Whether this is a pointer device. It decides two things about the emoji
  // panel: where it sits, and whether picking from it refocuses the input —
  // on a phone that would summon the soft keyboard over the panel that is
  // being used.
  const pointer = useMedia('(hover: hover) and (pointer: fine)')

  /*
   * Remember where the caret is.
   *
   * The composer is a contenteditable, so the insertion point is a DOM Range,
   * and it is gone the moment focus moves — which is exactly what opening a
   * panel and tapping something in it does. selectionchange is the only event
   * that fires for every way the caret can move: typing, clicking, arrow keys,
   * and the phone's own text handles.
   */
  useEffect(() => {
    const remember = () => {
      const node = inputRef.current
      const selection = typeof getSelection === 'function' ? getSelection() : null
      if (!node || !selection || selection.rangeCount === 0) return
      const range = selection.getRangeAt(0)
      if (node.contains(range.commonAncestorContainer)) caret.current = range.cloneRange()
    }
    document.addEventListener('selectionchange', remember)
    return () => document.removeEventListener('selectionchange', remember)
  }, [])

  /* --- restore drafts and editing text --- */

  useEffect(() => {
    if (!room) return
    if (editing) {
      setDraft(editing.text || '')
      if (inputRef.current) {
        inputRef.current.innerText = editing.text || ''
        focusEnd(inputRef.current)
      }
      return
    }
    let alive = true
    store.client.getDraft(room.id).then((text) => {
      if (!alive) return
      setDraft(text || '')
      if (inputRef.current) inputRef.current.innerText = text || ''
    })
    return () => { alive = false }
  }, [room && room.id, editing && editing.id])

  /* --- a card for the first link, fetched while typing --- */

  useEffect(() => {
    if (editing) return
    const url = previewableUrl(draft)
    const base = store.client.registryUrl
    if (!url || !base || store.client.settings.linkPreviews === false) {
      if (preview) setPreview(null)
      return
    }
    if (preview && preview.url === url) return
    let alive = true
    const timer = setTimeout(() => {
      fetchPreview(base, url)
        .then((data) => { if (alive && data) setPreview({ url, data, dismissed: false }) })
        .catch(() => {})
    }, 700)
    return () => { alive = false; clearTimeout(timer) }
  }, [draft, editing])

  /* --- persist drafts --- */

  useEffect(() => {
    if (!room || editing) return
    const id = setTimeout(() => store.client.saveDraft(room.id, draft).catch(() => {}), 400)
    return () => clearTimeout(id)
  }, [draft, room && room.id, editing])

  const onInput = useCallback((event) => {
    const text = event.currentTarget.innerText
    setDraft(text)
    if (!room || !store.client.settings.typingIndicators) return
    const now = Date.now()
    if (now - lastTyping.current > TYPING_THROTTLE && text.trim()) {
      lastTyping.current = now
      room.sendTyping(true)
    }
  }, [room])

  async function send () {
    const text = draft.trim()
    if (!text && attachments.length === 0) return
    if (editing) return saveEdit(text)

    /*
     * Handed over, and the composer is free again at once.
     *
     * Writing a message down is not instant everywhere: in Safari on an
     * iPhone it could take several seconds. The button used to spin for all
     * of that and ignore the next press — and when the first message was
     * finally through it cleared the input, taking with it whatever had been
     * typed in the meantime. So the message is taken as it stands, shown in
     * the chat as on its way (see PendingRow), and queued behind any still
     * going out; the input is cleared now, not later.
     */
    const job = {
      key: 'pending-' + Date.now() + '-' + Math.random().toString(36).slice(2),
      room,
      text,
      files: attachments,
      replyTo,
      card: preview && !preview.dismissed ? preview : null
    }
    setDraft('')
    if (inputRef.current) inputRef.current.innerText = ''
    if (attachments.length) setAttachments([])
    setPreview(null)
    onClearContext()
    store.client.saveDraft(room.id, '').catch(() => {})
    return enqueue(job)
  }

  /** A command for the bot in this chat: "/start", "/help". Sent as typed. */
  function sendCommand (command) {
    setCommandsOpen(false)
    setDraft('')
    if (inputRef.current) inputRef.current.innerText = ''
    return enqueue({
      key: 'pending-' + Date.now() + '-' + Math.random().toString(36).slice(2),
      room,
      text: '/' + command,
      files: [],
      replyTo: null,
      card: null
    })
  }

  async function enqueue (job) {
    if (onPending) {
      onPending({ add: { key: job.key, roomId: job.room.id, text: job.text, files: job.files.map((a) => a.name), at: clockNow() } })
    }

    const run = queue.current.then(() => deliver(job))
    queue.current = run.catch(() => {})
    try {
      const id = await run
      if (onPending) onPending({ sent: job.key, id })
    } catch (err) {
      if (onPending) onPending({ drop: job.key })
      toast(err.message || t('err.generic'), { error: true })
      // Back where it was typed — unless the chat changed, or something new
      // is already being typed there.
      if (roomRef.current !== job.room) return
      const node = inputRef.current
      if (job.text && !err.textSent && node && !node.innerText.trim()) {
        node.innerText = job.text
        setDraft(job.text)
      }
      if (err.unsent && err.unsent.length) setAttachments((now) => now.length ? now : err.unsent)
    }
  }

  /** @returns the id of the last message it posted */
  async function deliver ({ key, room, text, files, replyTo, card }) {
    if (files.length) {
      let last = null
      for (let i = 0; i < files.length; i++) {
        const item = files[i]
        try {
          last = await room.sendFile(
            { data: item.bytes, name: item.name, type: item.mime, meta: item.meta },
            {
              text: i === 0 ? text : '',
              replyTo: i === 0 && replyTo ? replyTo.id : null,
              kind: item.kind,
              // Matches the row on screen if the room keeps it for later.
              localKey: i === 0 ? key : null
            }
          )
        } catch (err) {
          // What is left to send, so a retry does not post the rest twice.
          err.unsent = files.slice(i)
          err.textSent = i > 0
          throw err
        }
      }
      return last
    }
    const parsed = parseShorthand(text)
    // The card goes only with the link it was made for, and only if it was
    // not waved away.
    const extra = card && parsed.text.includes(card.url) ? { preview: card.data } : null
    return room.send({
      text: parsed.text,
      entities: parsed.entities,
      replyTo: replyTo ? replyTo.id : null,
      extra,
      // Matches the row on screen if the room keeps it for later.
      localKey: key
    })
  }

  async function saveEdit (text) {
    if (sending) return
    setSending(true)
    try {
      const parsed = parseShorthand(text)
      await room.edit(editing.id, parsed.text, parsed.entities)
      onClearContext()
      setDraft('')
      if (inputRef.current) inputRef.current.innerText = ''
      await store.client.saveDraft(room.id, '')
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    } finally {
      setSending(false)
    }
  }

  /**
   * Put an emoji where the caret was.
   *
   * Not execCommand('insertText'): it is deprecated, it needs the element
   * focused, and in the Android WebView the focus() that precedes it lands the
   * caret at the start — so an emoji chosen halfway through a sentence went to
   * the front of it, when it arrived at all. Writing the Range directly works
   * whether or not the field has focus, which is what lets the panel behave
   * like a keyboard on a phone.
   */
  function insertEmoji (emoji) {
    const node = inputRef.current
    if (!node) return

    if (pointer) node.focus({ preventScroll: true })

    let range = caret.current
    if (!range || !node.contains(range.startContainer)) {
      range = document.createRange()
      range.selectNodeContents(node)
      range.collapse(false)
    }

    range.deleteContents()
    const text = document.createTextNode(emoji)
    range.insertNode(text)
    range.setStartAfter(text)
    range.collapse(true)
    caret.current = range.cloneRange()

    const selection = typeof getSelection === 'function' ? getSelection() : null
    if (selection && pointer) {
      selection.removeAllRanges()
      selection.addRange(range)
    }

    setDraft(node.innerText)
  }

  function toggleEmoji () {
    setAttachOpen(false)
    setEmojiOpen((open) => {
      // On a phone the panel takes the keyboard's place, so the keyboard has
      // to go: it would otherwise sit on top of what was just opened.
      if (!open && !pointer && inputRef.current) inputRef.current.blur()
      return !open
    })
  }

  /* --- the attach menu --- */

  /**
   * Ask for one kind of file at a time.
   *
   * A single "attach" button hands the system picker no filter, and on Android
   * that means the generic file browser: finding a photo in it takes several
   * taps through Downloads and folder names, when the gallery is one tap away
   * if you say what you are after. So the menu is not decoration — each entry
   * sets `accept` on the same input, which is what routes the picker to the
   * gallery, the music library or the document list.
   *
   * A desktop file dialog has none of that problem and shows every file
   * anyway, so there it stays two entries: a file, or a poll.
   */
  function pickFiles (accept, { original = false } = {}) {
    const input = fileRef.current
    setAttachOpen(false)
    if (!input) return
    input.accept = accept
    pickOriginal.current = original
    input.click()
  }

  const fullMenu = (store.platform && store.platform.kind === 'android') || !pointer

  const attachItems = [
    fullMenu && {
      id: 'media',
      label: t('attach.media'),
      icon: Icon.gallery(20),
      tone: 'tone-media',
      onSelect: () => pickFiles('image/*,video/*')
    },
    fullMenu && {
      id: 'music',
      label: t('attach.music'),
      icon: Icon.music(20),
      tone: 'tone-music',
      onSelect: () => pickFiles('audio/*')
    },
    fullMenu && {
      id: 'docs',
      label: t('attach.documents'),
      icon: Icon.doc(20),
      tone: 'tone-docs',
      // A photo picked as a document goes as it is, full size — that is
      // what choosing "documents" over "photos" means.
      onSelect: () => pickFiles(DOCUMENT_TYPES, { original: true })
    },
    {
      id: 'files',
      label: t('attach.files'),
      icon: Icon.file(20),
      tone: 'tone-files',
      onSelect: () => pickFiles('')
    },
    {
      id: 'poll',
      label: t('attach.poll'),
      icon: Icon.poll(20),
      tone: 'tone-poll',
      onSelect: () => {
        setAttachOpen(false)
        store.showDialog({ type: 'poll', roomId: room.id })
      }
    }
  ].filter(Boolean)

  /* --- the picker said no --- */

  useEffect(() => {
    const onFailure = (event) => {
      const reason = event.detail ? String(event.detail).slice(0, 120) : ''
      toast(reason ? t('attach.pickerFailed') + ': ' + reason : t('attach.pickerFailed'), { error: true })
    }
    window.addEventListener('pine:picker-failed', onFailure)
    return () => window.removeEventListener('pine:picker-failed', onFailure)
  }, [toast])

  /* --- dismissing the attach menu --- */

  useEffect(() => {
    if (!attachOpen) return
    const onPointerDown = (event) => {
      if (attachRef.current && attachRef.current.contains(event.target)) return
      // The picker is a native dialog: while it is open the click that opened
      // it has already been handled, and anything landing here afterwards is
      // outside the menu.
      setAttachOpen(false)
    }
    const onKey = (event) => { if (event.key === 'Escape') setAttachOpen(false) }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKey)
    const release = store.pushBack(() => { setAttachOpen(false); return true })

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKey)
      release()
    }
  }, [attachOpen, store])

  /* --- dismissing the emoji panel --- */

  useEffect(() => {
    if (!emojiOpen) return

    const onPointerDown = (event) => {
      if (emojiRef.current && emojiRef.current.contains(event.target)) return
      // The toggle is excluded so its own click is not first undone here and
      // then reapplied by the button, which left the panel stuck open.
      if (emojiButtonRef.current && emojiButtonRef.current.contains(event.target)) return
      setEmojiOpen(false)
    }
    const onKey = (event) => { if (event.key === 'Escape') setEmojiOpen(false) }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKey)
    // Back closes the panel before it closes the chat.
    const release = store.pushBack(() => { setEmojiOpen(false); return true })

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKey)
      release()
    }
  }, [emojiOpen, store])

  function onKeyDown (event) {
    if (event.key === 'Escape' && (replyTo || editing)) {
      onClearContext()
      return
    }
    const enterSends = store.client.settings.enterToSend
    if (event.key === 'Enter' && !event.shiftKey && enterSends && !event.isComposing) {
      event.preventDefault()
      send()
    }
    if (event.key === 'Enter' && event.ctrlKey && !enterSends) {
      event.preventDefault()
      send()
    }
  }

  async function onPaste (event) {
    const items = [...(event.clipboardData ? event.clipboardData.items : [])]
    const files = items.filter((i) => i.kind === 'file').map((i) => i.getAsFile()).filter(Boolean)
    if (files.length) {
      event.preventDefault()
      await addFiles(files)
      return
    }
    // Paste as plain text so foreign markup never enters the message.
    event.preventDefault()
    const text = event.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  async function addFiles (files, { original = false } = {}) {
    const prepared = []
    let failed = 0

    for (const file of files) {
      try {
        // Read the bytes first. On Android the File is backed by a content
        // URI whose grant belongs to the picker result, and everything else
        // here is a nicety by comparison — which is also why the preview and
        // the re-encoding below work from these bytes, not from the File.
        let bytes = new Uint8Array(await file.arrayBuffer())
        let name = file.name || 'file'
        let mime = file.type || 'application/octet-stream'
        let meta = {}
        const kind = kindForMime(mime)

        if (kind === KIND.IMAGE || kind === KIND.GIF) {
          // Re-encoded unless it was picked as a document; see media-prep.js.
          const photo = await preparePhoto(bytes, { name, mime, compress: !original })
          if (photo) {
            bytes = photo.bytes
            name = photo.name
            mime = photo.mime
            meta = { width: photo.width, height: photo.height, thumb: photo.thumb }
          }
        } else if (kind === KIND.VIDEO || kind === KIND.AUDIO) {
          meta = kind === KIND.VIDEO
            ? await describeVideo(bytes, { mime })
            : await describeMedia(file, kind)
        }

        prepared.push({
          id: Math.random().toString(36).slice(2),
          bytes,
          name,
          mime,
          size: bytes.byteLength,
          kind: kindForMime(mime),
          meta: { name, mime, ...meta },
          preview: kind === KIND.IMAGE || kind === KIND.GIF
            ? URL.createObjectURL(new Blob([bytes], { type: mime }))
            : null
        })
      } catch {
        // One unreadable file must not take the rest of the selection with
        // it, and picking a file that then does nothing at all is the worst
        // possible outcome — say so.
        failed++
      }
    }

    if (prepared.length) setAttachments((list) => [...list, ...prepared])
    if (failed) toast(t('attach.unreadable', { n: failed }), { error: true })
  }

  /* --- voice notes --- */

  async function startRecording () {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      })
      const mime = pickAudioMime()
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      const chunks = []
      const peaks = []

      const context = new (window.AudioContext || window.webkitAudioContext)()
      const analyser = context.createAnalyser()
      analyser.fftSize = 512
      context.createMediaStreamSource(stream).connect(analyser)
      const buffer = new Float32Array(analyser.fftSize)

      const sampler = setInterval(() => {
        analyser.getFloatTimeDomainData(buffer)
        let peak = 0
        for (const v of buffer) peak = Math.max(peak, Math.abs(v))
        peaks.push(peak)
      }, 90)

      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data) }
      recorder.start(200)

      const started = Date.now()
      const state = {
        recorder,
        stream,
        chunks,
        peaks,
        started,
        sampler,
        context,
        timer: setInterval(() => setRecording((r) => (r ? { ...r, seconds: (Date.now() - started) / 1000 } : r)), 200),
        seconds: 0,
        cancelled: false
      }
      setRecording(state)

      setTimeout(() => {
        if (recorder.state === 'recording') stopRecording(state, false)
      }, MAX_VOICE_SECONDS * 1000)
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    }
  }

  async function stopRecording (state, cancelled) {
    if (!state) return
    clearInterval(state.timer)
    clearInterval(state.sampler)

    const finished = new Promise((resolve) => { state.recorder.onstop = resolve })
    if (state.recorder.state !== 'inactive') state.recorder.stop()
    await finished

    for (const track of state.stream.getTracks()) track.stop()
    state.context.close().catch(() => {})
    setRecording(null)

    if (cancelled || state.chunks.length === 0) return
    const seconds = Math.round((Date.now() - state.started) / 1000)
    if (seconds < 1) return

    const blob = new Blob(state.chunks, { type: state.recorder.mimeType || 'audio/webm' })
    const bytes = new Uint8Array(await blob.arrayBuffer())

    try {
      await room.sendFile({
        data: bytes,
        name: 'voice-' + Date.now() + '.webm',
        type: blob.type,
        meta: {
          name: 'voice message',
          mime: blob.type,
          duration: seconds,
          waveform: computeWaveform(state.peaks, 34)
        }
      }, { kind: KIND.AUDIO, replyTo: replyTo ? replyTo.id : null })
      onClearContext()
    } catch (err) {
      toast(err.message, { error: true })
    }
  }

  /* --- render --- */

  // Something to say instead of a message field: the assistant to somebody
  // who has not been given access to it, for one.
  if (notice) {
    return html`
      <div class="composer">
        <div class="composer-notice">${notice}</div>
      </div>`
  }

  // A chat with a bot nobody has said anything in yet starts with its
  // /start, as everywhere else bots exist — one button, not a blank field.
  if (startable && canPost) {
    return html`
      <div class="composer">
        <button class="btn btn-primary btn-block bot-start" onClick=${() => sendCommand('start')}>
          ${t('bot.start')}
        </button>
      </div>`
  }

  if (!canPost) {
    return html`
      <div class="composer">
        <div class="row center dim small" style="padding:10px">${t('conv.readOnly')}</div>
      </div>`
  }

  const hasContent = draft.trim().length > 0 || attachments.length > 0

  // The bot's commands: all of them from the button, or the ones matching
  // what has been typed after a "/".
  const commands = bot && Array.isArray(bot.commands) ? bot.commands : []
  const typedCommand = /^\/[a-z0-9_]*$/i.test(draft.trim()) ? draft.trim().slice(1).toLowerCase() : null
  const shownCommands = commandsOpen
    ? commands
    : typedCommand !== null ? commands.filter((c) => c.command.startsWith(typedCommand)) : []

  return html`
    <div class="composer"
         onDragOver=${(e) => e.preventDefault()}
         onDrop=${(e) => { e.preventDefault(); addFiles([...e.dataTransfer.files]) }}>

      ${(replyTo || editing) && html`
        <div class="composer-context">
          ${editing ? Icon.edit(15) : Icon.reply(15)}
          <div class="grow" style="min-width:0">
            <div class="xs strong">${editing ? t('conv.editing') : t('conv.replyTo')}</div>
            <div class="small dim truncate">${(editing || replyTo).text || '…'}</div>
          </div>
          <button class="icon-btn" onClick=${onClearContext}>${Icon.close(16)}</button>
        </div>`}

      ${preview && !preview.dismissed && !editing && html`
        <div class="composer-context">
          <div class="composer-preview grow">
            ${preview.data.image && html`<img src=${'data:image/jpeg;base64,' + preview.data.image} alt="" />`}
            <div style="min-width:0">
              <div class="xs strong truncate" style="color:var(--accent)">${preview.data.site || preview.url}</div>
              <div class="small truncate">${preview.data.title || preview.data.description}</div>
            </div>
          </div>
          <button class="icon-btn" aria-label=${t('action.close')}
                  onClick=${() => setPreview({ ...preview, dismissed: true })}>${Icon.close(16)}</button>
        </div>`}

      ${shownCommands.length > 0 && html`
        <div class="command-list" role="listbox">
          ${shownCommands.map((c) => html`
            <button key=${c.command} class="command-item" role="option" onClick=${() => sendCommand(c.command)}>
              <span class="command-name">/${c.command}</span>
              <span class="command-desc truncate">${c.description}</span>
            </button>`)}
        </div>`}

      ${attachments.length > 0 && html`
        <div class="attach-preview">
          ${attachments.map((item) => html`
            <div key=${item.id} class="attach-thumb" title=${item.name + ' · ' + fileSize(item.size)}>
              ${item.preview
                ? html`<img src=${item.preview} alt="" />`
                : html`<span class="dim">${Icon.file(22)}</span>`}
              <button class="remove" onClick=${() => setAttachments((l) => l.filter((x) => x.id !== item.id))}>
                ${Icon.close(12)}
              </button>
            </div>`)}
        </div>`}

      <div class="composer-row">
        ${!recording && commands.length > 0 && html`
          <button class=${cx('icon-btn', commandsOpen && 'active')} title=${t('bot.commands')}
                  aria-expanded=${String(commandsOpen)}
                  onClick=${() => { setCommandsOpen((v) => !v); setAttachOpen(false); setEmojiOpen(false) }}>
            ${Icon.slash(20)}
          </button>`}
        ${!recording && html`
          <button class=${cx('icon-btn', attachOpen && 'active')} title=${t('attach.title')}
                  aria-expanded=${String(attachOpen)}
                  onClick=${() => { setAttachOpen((v) => !v); setEmojiOpen(false) }}>
            ${Icon.attach(20)}
          </button>`}

        ${recording
          ? html`
            <div class="recording-bar">
              <span class="pip"></span>
              <span class="grow">${t('conv.recording')} · ${duration(recording.seconds)}</span>
              <button class="btn btn-sm btn-ghost" onClick=${() => stopRecording(recording, true)}>
                ${t('action.cancel')}
              </button>
            </div>`
          : html`
            <div class="composer-input" contenteditable role="textbox" aria-multiline="true"
                 ref=${inputRef}
                 data-placeholder=${t('conv.placeholder')}
                 onInput=${onInput}
                 onKeyDown=${onKeyDown}
                 onPaste=${onPaste}
                 onBlur=${() => room && room.sendTyping(false)}></div>`}

        ${!recording && html`
          <button ref=${emojiButtonRef} class=${cx('icon-btn', emojiOpen && 'active')}
                  aria-label=${t('emoji.search')} onClick=${toggleEmoji}>
            ${Icon.emoji(20)}
          </button>`}

        ${hasContent || editing
          ? html`
            <button class="send-btn" disabled=${sending} onClick=${send} aria-label="Send">
              ${sending ? html`<div class="spinner"></div>` : (editing ? Icon.check(20) : Icon.send(20))}
            </button>`
          : html`
            <button class=${cx('send-btn', recording && 'recording')}
                    onClick=${() => (recording ? stopRecording(recording, false) : startRecording())}
                    aria-label=${t('conv.recording')}>
              ${recording ? Icon.send(20) : Icon.mic(20)}
            </button>`}
      </div>

      ${attachOpen && html`
        <div class="attach-dock" ref=${attachRef}>
          ${attachItems.map((item) => html`
            <button key=${item.id} class="attach-option" onClick=${item.onSelect}>
              <span class=${'glyph ' + item.tone}>${item.icon}</span>
              <span class="label">${item.label}</span>
            </button>`)}
        </div>`}

      ${emojiOpen && html`
        <div class="emoji-dock" ref=${emojiRef}>
          <${EmojiPicker} onPick=${insertEmoji} />
        </div>`}

      <!-- The value is cleared only once the bytes have been read: on Android
           the File objects are backed by the picker's content URIs, and
           resetting the input first can pull them out from under the read. -->
      <input ref=${fileRef} type="file" multiple class="hidden"
             onChange=${(e) => {
               const input = e.target
               const picked = [...input.files]
               addFiles(picked, { original: pickOriginal.current }).finally(() => { input.value = '' })
             }} />
    </div>`
}

function focusEnd (node) {
  node.focus()
  const range = document.createRange()
  range.selectNodeContents(node)
  range.collapse(false)
  const selection = getSelection()
  selection.removeAllRanges()
  selection.addRange(range)
}

function pickAudioMime () {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
  for (const mime of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) return mime
  }
  return ''
}

/** Read the dimensions or duration the message bubble needs to size itself. */
/**
 * Width, height and duration, if the browser will tell us.
 *
 * All of it is optional — it decides the placeholder shape while a photo
 * downloads, nothing more — and every path here is bounded, because none of
 * these events is guaranteed to fire. A phone photo in HEIC or a video in a
 * codec Chromium will not open can leave both `onload` and `onerror` silent,
 * and the promise that never settled took the whole send with it: the file
 * was picked, nothing appeared in the composer, and there was no error to
 * explain why. A file with unknown dimensions still sends perfectly well.
 */
async function describeMedia (file, kind) {
  if (kind === KIND.IMAGE || kind === KIND.GIF) {
    return probe((resolve) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      const done = (value) => { resolve(value); URL.revokeObjectURL(url) }
      img.onload = () => done({ width: img.naturalWidth, height: img.naturalHeight })
      img.onerror = () => done({})
      img.src = url
    })
  }
  if (kind === KIND.VIDEO || kind === KIND.AUDIO) {
    return probe((resolve) => {
      const el = document.createElement(kind === KIND.VIDEO ? 'video' : 'audio')
      const url = URL.createObjectURL(file)
      const done = (value) => { resolve(value); URL.revokeObjectURL(url) }
      el.preload = 'metadata'
      el.onloadedmetadata = () => done({
        duration: Math.round(el.duration || 0),
        width: el.videoWidth || 0,
        height: el.videoHeight || 0
      })
      el.onerror = () => done({})
      el.src = url
    })
  }
  return {}
}

const PROBE_TIMEOUT = 4000

function probe (start) {
  return new Promise((resolve) => {
    let settled = false
    const finish = (value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(value || {})
    }
    const timer = setTimeout(() => finish({}), PROBE_TIMEOUT)
    try {
      start(finish)
    } catch {
      finish({})
    }
  })
}
